import { Box, Button, Stack, SxProps, Theme, Typography } from '@mui/material';
import { Info } from 'lucide-react';

// Deliberate brand accent from the redesign mockup (--btn-purple); the V2
// theme has no equivalent filled-purple token yet, so the Save button carries
// the literal value until one is added.
const SAVE_PURPLE = '#7231e2';

const saveBarSx: SxProps<Theme> = {
  position: 'sticky',
  bottom: 0,
  zIndex: 4,
  mx: -2.25,
  mb: -2,
  mt: 2,
  px: 2.25,
  py: 1.5,
  bgcolor: 'custom.OV.sys.surfaceContainer',
  borderTop: '1px solid',
  borderColor: 'custom.OV.sys.outlineVariant',
  borderRadius: '0 0 0.75rem 0.75rem',
  boxShadow: '0 -0.375rem 1rem rgba(0, 0, 0, 0.08)',
};

const messageIconSx: SxProps<Theme> = {
  display: 'inline-flex',
  color: 'custom.OV.sys.secondary',
  '& svg': { width: '0.9375rem', height: '0.9375rem' },
};

const buttonBaseSx: SxProps<Theme> = {
  height: '2.125rem',
  minHeight: '2.125rem',
  borderRadius: '0.5rem',
  px: 1.75,
  fontSize: '0.78rem',
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: 'none',
};

const discardButtonSx: SxProps<Theme> = {
  color: 'custom.OV.sys.onSurface',
  borderColor: 'custom.OV.sys.outlineVariant',
  bgcolor: 'custom.OV.sys.surfaceContainer',
  '&:hover': {
    borderColor: 'custom.OV.sys.outline',
    bgcolor: 'custom.OV.sys.surfaceContainerHigh',
  },
};

const saveButtonSx: SxProps<Theme> = {
  px: 2,
  color: 'common.white',
  bgcolor: SAVE_PURPLE,
  '&:hover': { bgcolor: SAVE_PURPLE, filter: 'brightness(1.06)', boxShadow: 'none' },
};

export const SlaRoutingSaveBar = ({
  isSaving,
  onSave,
  onDiscard,
}: {
  isSaving: boolean;
  onSave: () => void;
  onDiscard: () => void;
}) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="space-between"
    gap={1.5}
    sx={saveBarSx}
  >
    <Stack direction="row" alignItems="center" gap={0.75} minWidth={0}>
      <Box aria-hidden sx={messageIconSx}>
        <Info />
      </Box>
      <Typography variant="body2" fontWeight={500} color="custom.OV.sys.onSurfaceVariant" noWrap>
        Unsaved routing changes
      </Typography>
    </Stack>
    <Stack direction="row" gap={1} flexShrink={0}>
      <Button variant="outlined" onClick={onDiscard} disabled={isSaving} sx={[buttonBaseSx, discardButtonSx]}>
        Discard
      </Button>
      <Button variant="contained" onClick={onSave} disabled={isSaving} sx={[buttonBaseSx, saveButtonSx]}>
        {isSaving ? 'Saving...' : 'Save changes'}
      </Button>
    </Stack>
  </Stack>
);
